import {
  foldedStageStatus,
  IMAGE_CHECK_FOLD_TARGET,
  IMAGE_CHECK_STAGE,
  isHiddenStage,
  visibleStage,
} from '../constants/hidden-stages';
import type { RunProgress, RunRead, StageProgress } from '../types/run.api.types';

import { hasField } from './catalog-capabilities';
import { stageCount } from './run-format';
import { groupStagesByPhase, type RunPhase } from './run-phases';

/** One phase's state as a person reads it (Q26) — never a raw stage status. */
export type PhaseState = 'done' | 'running' | 'waiting' | 'failed' | 'notStarted' | 'skipped';

/** One step of a phase's product flow ("40 imported", "1 held back"); `aside` steps join with "·". */
export interface FlowStep {
  key: string;
  count: number;
  aside?: boolean;
}

/** Everything one phase tile shows: state, its stages, the product flow, time and decisions. */
export interface PhaseSummary {
  phase: RunPhase;
  stages: StageProgress[];
  state: PhaseState;
  /** Summed elapsed time of the phase's stages that have run. */
  elapsedMs: number | null;
  flow: FlowStep[];
  /** Gated stages in this phase — the ⚑ "you decide" points. */
  decisions: string[];
  /** The stage whose gate is open right now, when it is in this phase. */
  openDecision: string | null;
}

const FAILED = new Set(['FAILED', 'RECONCILE_FAILED']);
const FINISHED = new Set(['COMPLETED', 'SKIPPED']);

/** A phase's state from its stages: any failure / open gate / running stage wins over "done". */
function phaseState(stages: StageProgress[]): PhaseState {
  if (stages.some((s) => FAILED.has(s.status))) return 'failed';
  if (stages.some((s) => s.status === 'AWAITING_GATE')) return 'waiting';
  if (stages.some((s) => s.status === 'RUNNING')) return 'running';
  if (stages.every((s) => s.status === 'SKIPPED')) return 'skipped';
  if (stages.every((s) => FINISHED.has(s.status))) return 'done';
  // Part done, part pending: the run is between two stages of this phase.
  if (stages.some((s) => FINISHED.has(s.status))) return 'running';
  return 'notStarted';
}

/** True once a stage has reported anything worth counting (a PENDING stage's zeros mean nothing). */
export function reached(stage: StageProgress | undefined): stage is StageProgress {
  return !!stage && stage.status !== 'PENDING';
}

/**
 * The product flow a phase tells (Q26: "40 imported → 14 in test arm → 13 curated · 1 held
 * back"), from the same per-stage counters the stages table shows (Q9), so numbers agree.
 */
function phaseFlow(phase: RunPhase, stages: StageProgress[], progress: RunProgress): FlowStep[] {
  const by = (code: string) => stages.find((s) => s.stage === code);
  const steps: FlowStep[] = [];
  const push = (key: string, count: number | undefined, aside = false) => {
    if (typeof count === 'number') steps.push({ key, count, ...(aside ? { aside } : {}) });
  };
  const failed = (stage: StageProgress) => {
    const n = stageCount(stage, 'products_failed');
    if (n) push('failed', n, true);
  };

  if (phase === 'PREPARE') {
    const importStage = by('IMPORT');
    // Extract's size is the honest "imported" figure; IMPORT's own counter only sums the
    // rows it attached for matching, which is 0 when everything was already in the catalog.
    if (reached(importStage)) {
      push('imported', progress.funnel?.extracted ?? stageCount(importStage, 'products_succeeded'));
    }
    const arm = by('ARM_ASSIGNMENT');
    if (reached(arm)) push('testArm', stageCount(arm, 'products_succeeded'));
    const curation = by('CURATION');
    if (reached(curation)) {
      push('curated', stageCount(curation, 'products_succeeded'));
      const heldBack = curation.funnelCounts?.flagged;
      if (heldBack) push('heldBack', heldBack, true);
    }
  } else if (phase === 'CHECK_QUALITY') {
    const score = by('PRE_ENRICH_SCORE');
    if (reached(score)) push('scored', stageCount(score, 'products_succeeded'));
    const sample = by('SAMPLE');
    if (reached(sample)) {
      push('sampled', stageCount(sample, 'products_succeeded'));
      failed(sample);
    }
  } else if (phase === 'GENERATE') {
    const enrich = by('ENRICHMENT');
    if (reached(enrich)) {
      push('enriched', stageCount(enrich, 'products_succeeded'));
      failed(enrich);
    }
  } else {
    const delivery = by('DELIVERY');
    if (progress.delivery || reached(delivery)) {
      push('delivered', progress.delivery?.delivered ?? stageCount(delivery, 'products_succeeded'));
      const n = progress.delivery?.failed ?? stageCount(delivery, 'products_failed');
      if (n) push('failed', n, true);
    }
  }
  return steps;
}

/** The newest attempt of a stage in a progress list (`progress.stages` may carry several). */
function newestAttempt(stages: StageProgress[], code: string): StageProgress | undefined {
  return stages
    .filter((s) => s.stage === code)
    .reduce<
      StageProgress | undefined
    >((acc, s) => (!acc || s.attempt > acc.attempt ? s : acc), undefined);
}

/**
 * A run's stages as the UI shows them: the hidden Image check (hidden-stages.ts) removed, its
 * outcome folded into Feed intake the way the Summary funnel folds it — worse status wins (a run
 * stuck or failed at image check reads as stuck / failed at Feed intake, never "done"), its failure
 * reason kept when Feed intake has none, its failed count and time added. Counters otherwise stay
 * Feed intake's own, so the progress card's figures keep matching the header and funnel.
 */
export function visibleProgressStages(stages: StageProgress[]): StageProgress[] {
  const hidden = newestAttempt(stages, IMAGE_CHECK_STAGE);
  const visible = stages.filter((s) => !isHiddenStage(s.stage));
  if (!hidden || (hidden.status === 'PENDING' && !hidden.startedAt)) return visible;
  return visible.map((s) => {
    if (s.stage !== IMAGE_CHECK_FOLD_TARGET) return s;
    const failed = (s.counts?.products_failed ?? 0) + (hidden.counts?.products_failed ?? 0);
    return {
      ...s,
      status: foldedStageStatus(s.status, hidden.status),
      failureReason: s.failureReason ?? hidden.failureReason ?? null,
      completedAt:
        hidden.completedAt ?? (hidden.status === 'COMPLETED' ? (s.completedAt ?? null) : null),
      elapsedMs:
        s.elapsedMs != null && hidden.elapsedMs != null
          ? s.elapsedMs + hidden.elapsedMs
          : (s.elapsedMs ?? null),
      ...(failed > 0 ? { counts: { ...s.counts, products_failed: failed } } : {}),
    };
  });
}

/**
 * True when the progress read carries `gatedStages` (§49); an older backend omits the key, and
 * the caller then reads `config.flow.gatedStages` from `GET catalog-runs/{id}` instead.
 */
export function hasProgressGatedStages(progress: RunProgress | undefined): boolean {
  return hasField(progress, 'gatedStages');
}

/** The run's gates in flow order: `progress.gatedStages` when sent, else the run's flow config. */
export function resolveGatedStages(
  progress: RunProgress | undefined,
  run: RunRead | undefined,
): readonly string[] | null {
  if (progress && hasProgressGatedStages(progress)) return progress.gatedStages ?? [];
  return run?.config?.flow?.gatedStages ?? null;
}

/**
 * Folds a run's stages into the four phase tiles (Q26): state, product flow, time spent and
 * the ⚑ decision points. `gatedStages` (`resolveGatedStages`) marks gates still ahead,
 * since `requiresGate` is only reliable once a stage has been entered.
 */
export function summarizePhases(
  progress: RunProgress,
  gatedStages: readonly string[] | null | undefined,
): PhaseSummary[] {
  const gate = progress.pendingGate?.stage;
  const openGate = gate ? visibleStage(gate) : null;
  return groupStagesByPhase(visibleProgressStages(progress.stages)).map(({ phase, stages }) => {
    const elapsed = stages.reduce((sum, s) => sum + (s.elapsedMs ?? 0), 0);
    const decisions = stages
      .filter((s) => s.requiresGate || (gatedStages ?? []).includes(s.stage))
      .map((s) => s.stage);
    return {
      phase,
      stages,
      state: phaseState(stages),
      elapsedMs: elapsed > 0 ? elapsed : null,
      flow: phaseFlow(phase, stages, progress),
      decisions,
      openDecision: openGate && stages.some((s) => s.stage === openGate) ? openGate : null,
    };
  });
}

/** Index of the phase the run is in now: the current stage's phase, else the first unfinished one. */
export function currentPhaseIndex(phases: PhaseSummary[], currentStage: string | null | undefined) {
  const current = currentStage ? visibleStage(currentStage) : currentStage;
  const byStage = phases.findIndex((p) => p.stages.some((s) => s.stage === current));
  if (byStage >= 0) return byStage;
  const unfinished = phases.findIndex((p) => p.state !== 'done' && p.state !== 'skipped');
  return unfinished >= 0 ? unfinished : Math.max(phases.length - 1, 0);
}

/** The first stage after `currentStage` that has not finished — what the "Next" line names.
 *  Never a hidden stage: at Feed intake the next step is Matching, not the hidden Image check, and
 *  a run AT the hidden stage counts from where it sits (so Next still says Matching). */
export function nextStage(
  stages: StageProgress[],
  currentStage: string | null | undefined,
): StageProgress | undefined {
  let from = stages.findIndex((s) => s.stage === currentStage);
  // A folded list has no row for the hidden stage — count from the stage it folded into.
  if (from === -1 && currentStage)
    from = stages.findIndex((s) => s.stage === visibleStage(currentStage));
  return stages.slice(from + 1).find((s) => !FINISHED.has(s.status) && !isHiddenStage(s.stage));
}
